/**
 * Safe localStorage utilities that handle cases where localStorage is not available or disabled
 */

interface StorageInterface {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

interface SafeStorage {
  getItem: (key: string) => string | null;
  setItem: (key: string, value: string) => boolean;
  removeItem: (key: string) => boolean;
}

const isStorageAvailable = (storage: StorageInterface): boolean => {
  try {
    const testKey = '__storage_test__';
    storage.setItem(testKey, 'test');
    storage.removeItem(testKey);
    return true;
  } catch (e) {
    return false;
  }
};

// localStorage utilities
export const safeLocalStorage: SafeStorage = {
  getItem: (key: string): string | null => {
    try {
      if (!isStorageAvailable(localStorage)) {
        return null;
      }
      return localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },

  setItem: (key: string, value: string): boolean => {
    try {
      if (!isStorageAvailable(localStorage)) {
        return false;
      }
      localStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  },

  removeItem: (key: string): boolean => {
    try {
      if (!isStorageAvailable(localStorage)) {
        return false;
      }
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },
};

// sessionStorage utilities
export const safeSessionStorage: SafeStorage = {
  getItem: (key: string): string | null => {
    try {
      if (!isStorageAvailable(sessionStorage)) {
        return null;
      }
      return sessionStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },

  setItem: (key: string, value: string): boolean => {
    try {
      if (!isStorageAvailable(sessionStorage)) {
        return false;
      }
      sessionStorage.setItem(key, value);
      return true;
    } catch (error) {
      return false;
    }
  },

  removeItem: (key: string): boolean => {
    try {
      if (!isStorageAvailable(sessionStorage)) {
        return false;
      }
      sessionStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },
};
